import i18next from "i18next"
import Swal from "sweetalert2"
import "./defaultAlert.css"

export const alertIcon = {
    ERROR: "error",
    SUCCESS: "success",
    WARNING: "warning"
}
const alertTitle = {
    ERROR: i18next.t('error'),
    SUCCESS: i18next.t('success'),
    WARNING: i18next.t('warning'),
}

const iconColor = {
    ERROR: "#ef4444",
    SUCCESS: "#22c55e",
    WARNING: "#f59e0b",
}

const NETWORK_ERROR_MESSAGE = "Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.";
const TIMEOUT_ERROR_MESSAGE = "O servidor demorou para responder. Aguarde alguns instantes e tente novamente.";

// Erros do Axios sem response não carregam uma mensagem confiável: podem ser queda de
// conexão, timeout, CORS ou indisponibilidade temporária. Não expomos "Network Error".
const getAlertText = (text) => {
    if (text && typeof text === "object" && !text.response) {
        if (text.code === "ECONNABORTED" || text.code === "ETIMEDOUT") {
            return TIMEOUT_ERROR_MESSAGE;
        }

        return NETWORK_ERROR_MESSAGE;
    }

    // Objeto de erro do Axios COM resposta: a mensagem útil está no corpo devolvido pelo
    // backend. Muitas telas chamam errorAlert(error) direto, e sem esta extração o Swal
    // recebia um objeto e o operador via só um erro genérico, sem o motivo real da recusa
    // ("Cobrança sem vínculo com o Asaas", por exemplo). Quem precisa tratar por status
    // continua usando ApiError.
    if (text && typeof text === "object" && text.response) {
        const data = text.response.data;
        const detailedErrors = Array.isArray(data?.errors) ? data.errors.filter(Boolean) : [];

        return detailedErrors.length > 0
            ? detailedErrors.join("\n")
            : data?.message || data?.detail || text.message
                || "Ocorreu um erro inesperado. Tente novamente.";
    }

    return text || "Ocorreu um erro inesperado. Tente novamente.";
}

// Config visual compartilhada por todos os alertas (ver defaultAlert.css) - variant
// define a cor do botão de confirmação (success/error/warning).
const themedConfig = (variant) => ({
    iconColor: iconColor[variant],
    showClass: { popup: "colmeia-swal-show" },
    hideClass: { popup: "" },
    buttonsStyling: false,
    customClass: {
        popup: "colmeia-swal-popup",
        icon: "colmeia-swal-icon",
        title: "colmeia-swal-title",
        htmlContainer: "colmeia-swal-html",
        actions: "colmeia-swal-actions",
        confirmButton: `colmeia-swal-confirm colmeia-swal-confirm--${variant.toLowerCase()}`,
        cancelButton: "colmeia-swal-cancel",
    },
})

export const errorAlert = (text) => {
    Swal.fire({
        title: alertTitle.ERROR,
        text: getAlertText(text),
        icon: alertIcon.ERROR,
        confirmButtonText: i18next.t('ok'),
        ...themedConfig("ERROR"),
    })
}

export const errorObjectAlert = (error) => {
    const responseData = error?.response?.data;
    const detailedErrors = Array.isArray(responseData?.errors)
        ? responseData.errors.filter(Boolean)
        : [];

    // Validações do backend trazem uma mensagem técnica genérica no campo "message" e
    // os motivos acionáveis em "errors". Mostrar primeiro os detalhes permite ao operador
    // corrigir o cadastro (ex.: sobrenome obrigatório) em vez de receber apenas "Error while
    // committing the transaction".
    const text = detailedErrors.length > 0
        ? detailedErrors.join('\n')
        : responseData?.message || (error?.response ? error.message : getAlertText(error));

    Swal.fire({
        title: alertTitle.ERROR,
        text: text,
        icon: alertIcon.ERROR,
        confirmButtonText: i18next.t('ok'),
        ...themedConfig("ERROR"),
    })
}

/**
 * Pergunta de sim ou não. onCancel é opcional: serve a quem precisa desfazer algo quando a
 * resposta é não, como reabrir o formulário que foi escondido para a pergunta aparecer.
 */
export const warnAlertYesOrNo = (text, onClickButton, onCancel) => {
    Swal.fire({
        title: alertTitle.WARNING,
        text: text,
        icon: alertIcon.WARNING,
        showCancelButton: true,
        confirmButtonText: i18next.t('yes'),
        cancelButtonText: i18next.t('no'),
        ...themedConfig("WARNING"),
    }).then((result) => {
        if (result.isConfirmed) {
            onClickButton();
        } else if (onCancel) {
            onCancel();
        }
    });
}

/**
 * Pergunta de sim ou não com um campo de texto opcional para o motivo.
 *
 * Serve a ações que precisam deixar rastro de por que foram feitas (liberar um cliente
 * penalizado, por exemplo). O texto vai para onConfirm; vazio chega como string vazia.
 *
 * @param {string} text pergunta exibida
 * @param {{placeholder?: string, onConfirm: Function}} options
 */
export const warnAlertWithReason = (text, { placeholder, onConfirm }) => {
    Swal.fire({
        title: alertTitle.WARNING,
        text: text,
        icon: alertIcon.WARNING,
        input: "textarea",
        inputPlaceholder: placeholder,
        inputAttributes: { maxlength: 500 },
        showCancelButton: true,
        confirmButtonText: i18next.t('yes'),
        cancelButtonText: i18next.t('no'),
        ...themedConfig("WARNING"),
    }).then((result) => {
        if (result.isConfirmed) {
            onConfirm((result.value || "").trim());
        }
    });
}

/**
 * Aviso com duas ações afirmativas, além de cancelar.
 *
 * Existe para a pergunta que não é sim/não: mover um atendimento confirmado tem dois
 * desfechos legítimos (o cliente já concordou, ou ainda não), e encadear dois diálogos para
 * decidir isso seria pior que um só com as duas saídas na frente.
 *
 * @param {string} text pergunta exibida
 * @param {{confirmText: string, denyText: string, onConfirm: Function, onDeny: Function}} options
 */
export const warnAlertTwoChoices = (text, { confirmText, denyText, onConfirm, onDeny }) => {
    Swal.fire({
        title: alertTitle.WARNING,
        text: text,
        icon: alertIcon.WARNING,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: confirmText,
        denyButtonText: denyText,
        cancelButtonText: i18next.t('cancel'),
        ...themedConfig("WARNING"),
        customClass: {
            ...themedConfig("WARNING").customClass,
            denyButton: "colmeia-swal-confirm colmeia-swal-confirm--warning",
        },
    }).then((result) => {
        if (result.isConfirmed) {
            onConfirm();
        } else if (result.isDenied) {
            onDeny();
        }
    });
}

export const warnAlert = (text) => {
    Swal.fire({
        title: alertTitle.WARNING,
        text: text,
        icon: alertIcon.WARNING,
        confirmButtonText: i18next.t('ok'),
        ...themedConfig("WARNING"),
    })
}

/**
 * Confirmação discreta, que aparece e some sozinha.
 *
 * Para quando a ação já foi confirmada antes e o resultado é visível na própria tela:
 * pedir um "OK" ali só cobra um clique para reconhecer algo que o operador está vendo.
 * Erro continua em alerta bloqueante, porque exige decisão.
 */
export const successToast = (text) => {
    Swal.fire({
        text,
        icon: alertIcon.SUCCESS,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true,
        iconColor: iconColor.SUCCESS,
        showClass: { popup: "colmeia-swal-show" },
        hideClass: { popup: "" },
        customClass: {
            popup: "colmeia-swal-popup colmeia-swal-toast",
            icon: "colmeia-swal-icon",
            htmlContainer: "colmeia-swal-html",
        },
    });
}

export const successAlert = (text, onClickButton) => {
    Swal.fire({
        title: alertTitle.SUCCESS,
        text: text,
        icon: alertIcon.SUCCESS,
        showCancelButton: false,
        confirmButtonText: i18next.t('ok'),
        ...themedConfig("SUCCESS"),
    }).then((result) => {
        if (result.isConfirmed) {
            if (onClickButton) {
                onClickButton();
            }
        }
    });
}
